import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import bg2 from '../assets/images/bg2.png';
import logoIzyko from '../assets/logos/izyko-black.png';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      let errorMessage = t('errors.invalidCredentials');
      
      // Handle different error formats
      const detail = err.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Pydantic validation error
          errorMessage = detail[0]?.msg || t('errors.invalidCredentials');
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile header */}
      <div className="lg:hidden p-4 flex items-center">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/')}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
      </div>

      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="hidden lg:inline-block mb-8">
            <img src={logoIzyko} alt="izyko" className="h-12 w-auto object-contain" />
          </Link>
          <div className="text-center lg:text-left mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-black mb-2">
              {t('auth.login.title')}
            </h1>
            <p className="text-muted-foreground font-medium">
              {t('auth.login.subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm font-medium" data-testid="login-error-message">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="font-bold">{t('auth.login.email')}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="vous@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-bold">{t('auth.login.password')}</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  required
                  className="pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" strokeWidth={2} />
                  ) : (
                    <Eye className="h-5 w-5" strokeWidth={2} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link 
                to="/reset-password" 
                className="text-sm font-bold text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-full py-6 text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t('auth.login.submit')
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground font-medium">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="text-foreground font-bold hover:underline">
              {t('auth.login.register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${bg2})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-background/20 to-transparent" />
      </div>
    </div>
  );
}
